import { useEffect, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { setItemToStore } from "@src/utility/helpers/utils";
import usePanelAbility from "@hooks/use-panel-ability";
import useAxios from "@hooks/use-axios";
import { ROLE_HOME_ROUTE } from "@src/router/routes";

// Backend role strings don't match this app's internal role vocabulary
// (ops/brand/publisher, used by ROLE_ABILITY and ROLE_HOME_ROUTE) — map
// them here, once, at the single point session role gets persisted.
const BACKEND_ROLE_MAP = {
  admin    : "brand",
  affiliate: "publisher"
};

/**
 * Completes the passwordless login: reads the `token` (credential) and
 * `email` query params from the magic-link URL, exchanges them for a
 * session via `login/verify`, then routes into the panel.
 */
const useLoginCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAxios({ base: "base" });
  const { handlePanelAbility } = usePanelAbility();
  const [status, setStatus] = useState("verifying");
  const hasRun = useRef(false);

  const email = searchParams.get("email");
  const token = searchParams.get("token");

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const verify = async () => {
      if (!email || !token) {
        setStatus("error");
        return;
      }

      try {
        const res = await axiosInstance.post("auth/v1/login/verify", {
          email,
          credential: token
        });
        const result = res.data || res;

        const rawRole = result.user?.role;
        const userInfo = {
          user: {
            id       : result.user?.userId,
            name     : result.user?.name || email.split("@")[0],
            email    : result.user?.email || email,
            role     : BACKEND_ROLE_MAP[rawRole] || rawRole,
            kycStatus: result.kycStatus
          },
          token: result.accessToken
        };
        // handlePanelAbility persists `userInfo` (user + token together)
        // under the "userInfo" key, and `token` separately under "token".
        handlePanelAbility({ userInfo });

        if (result.refreshToken) setItemToStore("refreshToken", result.refreshToken);
        setStatus("success");

        const role = userInfo.user.role;
        navigate(ROLE_HOME_ROUTE[role] || "/dashboard");
      } catch {
        setStatus("error");
      }
    };

    verify();
  }, [email, token]);

  return { status, email };
};

export default useLoginCallback;
